import { useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsDown,
  ChevronsUp,
} from 'lucide-react';
import { Button, Input, cn } from './ui';

export interface DualListOption {
  id: string;
  /** Shown in the first column; the thing somebody scans for. */
  label: string;
  /** Shown in the second column — a type, a name, whatever distinguishes two similar rows. */
  detail?: string;
}

/**
 * Two panes: everything available, and what has been chosen.
 *
 * Used wherever a route picks trunks or extensions. A multi-select listbox would be fewer lines,
 * but it cannot show what is selected without scrolling through what is not, and it cannot express
 * order at all — and for a trunk list the order IS the failover sequence, which is the most
 * consequential thing on the screen.
 *
 * Both panes search independently. With forty trunks and eight chosen, "which ones did I pick?" is
 * a question about the right-hand pane alone.
 */
export function DualList({
  options,
  selected,
  onChange,
  labelHeader = 'Name',
  detailHeader = 'Type',
  ordered = false,
  emptyHint = 'Nothing selected',
}: {
  options: readonly DualListOption[];
  /** Ordered when `ordered` is set — the caller keeps the order this returns. */
  selected: readonly string[];
  onChange: (next: string[]) => void;
  labelHeader?: string;
  detailHeader?: string;
  /** Show reorder controls, for lists where the order carries meaning. */
  ordered?: boolean;
  emptyHint?: string;
}) {
  const [availableQuery, setAvailableQuery] = useState('');
  const [selectedQuery, setSelectedQuery] = useState('');
  const [availableTicks, setAvailableTicks] = useState<string[]>([]);
  const [selectedTicks, setSelectedTicks] = useState<string[]>([]);

  const byId = useMemo(() => new Map(options.map((o) => [o.id, o])), [options]);

  const available = useMemo(() => {
    const needle = availableQuery.trim().toLowerCase();
    return options
      .filter((o) => !selected.includes(o.id))
      .filter((o) => !needle || `${o.label} ${o.detail ?? ''}`.toLowerCase().includes(needle));
  }, [options, selected, availableQuery]);

  const chosen = useMemo(() => {
    const needle = selectedQuery.trim().toLowerCase();
    // Mapped from `selected` rather than filtered from `options`, so the caller's order is what
    // renders — filtering options would silently sort by whatever order they arrived in.
    return selected
      .map((id) => byId.get(id))
      .filter((o): o is DualListOption => o !== undefined)
      .filter((o) => !needle || `${o.label} ${o.detail ?? ''}`.toLowerCase().includes(needle));
  }, [selected, byId, selectedQuery]);

  const add = () => {
    onChange([...selected, ...availableTicks.filter((id) => !selected.includes(id))]);
    setAvailableTicks([]);
  };

  const remove = () => {
    onChange(selected.filter((id) => !selectedTicks.includes(id)));
    setSelectedTicks([]);
  };

  /** Move the ticked rows as a block, so a multi-row selection stays together. */
  const move = (to: 'top' | 'up' | 'down' | 'bottom') => {
    if (selectedTicks.length === 0) return;
    const rest = selected.filter((id) => !selectedTicks.includes(id));
    const picked = selected.filter((id) => selectedTicks.includes(id));

    if (to === 'top') return onChange([...picked, ...rest]);
    if (to === 'bottom') return onChange([...rest, ...picked]);

    const first = selected.findIndex((id) => selectedTicks.includes(id));
    const target = to === 'up' ? Math.max(0, first - 1) : Math.min(rest.length, first + 1);
    return onChange([...rest.slice(0, target), ...picked, ...rest.slice(target)]);
  };

  const Pane = ({
    rows,
    ticks,
    setTicks,
    query,
    setQuery,
    title,
  }: {
    rows: readonly DualListOption[];
    ticks: string[];
    setTicks: (next: string[]) => void;
    query: string;
    setQuery: (next: string) => void;
    title: string;
  }) => (
    <div className="flex min-w-0 flex-1 flex-col rounded-[--radius] border border-border">
      <div className="flex items-center justify-between border-b border-border px-3 py-2 text-xs">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            className="size-3.5"
            checked={rows.length > 0 && ticks.length === rows.length}
            onChange={(e) => setTicks(e.target.checked ? rows.map((r) => r.id) : [])}
          />
          <span className="text-muted-foreground">{rows.length} items</span>
        </label>
        <span className="font-medium">{title}</span>
      </div>

      <div className="border-b border-border p-2">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search here"
          className="h-8"
        />
      </div>

      <div className="grid grid-cols-[1fr_auto] gap-2 border-b border-border px-3 py-1.5 text-xs font-medium text-muted-foreground">
        <span>{labelHeader}</span>
        <span>{detailHeader}</span>
      </div>

      <div className="max-h-64 min-h-32 overflow-y-auto">
        {rows.length === 0 ? (
          <p className="px-3 py-8 text-center text-sm text-muted-foreground">{emptyHint}</p>
        ) : (
          rows.map((row) => (
            <label
              key={row.id}
              className={cn(
                'grid cursor-pointer grid-cols-[auto_1fr_auto] items-center gap-2 px-3 py-1.5 text-sm hover:bg-accent',
                ticks.includes(row.id) && 'bg-accent/60',
              )}
            >
              <input
                type="checkbox"
                className="size-3.5"
                checked={ticks.includes(row.id)}
                onChange={(e) =>
                  setTicks(
                    e.target.checked ? [...ticks, row.id] : ticks.filter((id) => id !== row.id),
                  )
                }
              />
              <span className="truncate">{row.label}</span>
              <span className="truncate text-xs text-muted-foreground">{row.detail ?? ''}</span>
            </label>
          ))
        )}
      </div>
    </div>
  );

  return (
    <div className="flex flex-wrap items-start gap-2 sm:flex-nowrap">
      <Pane
        rows={available}
        ticks={availableTicks}
        setTicks={setAvailableTicks}
        query={availableQuery}
        setQuery={setAvailableQuery}
        title="Available"
      />

      <div className="flex flex-row gap-1 self-center sm:flex-col">
        <Button
          variant="secondary"
          className="h-8 px-2"
          onClick={add}
          disabled={availableTicks.length === 0}
          aria-label="Add selected"
        >
          <ChevronRight className="size-4" />
        </Button>
        <Button
          variant="secondary"
          className="h-8 px-2"
          onClick={remove}
          disabled={selectedTicks.length === 0}
          aria-label="Remove selected"
        >
          <ChevronLeft className="size-4" />
        </Button>
      </div>

      <Pane
        rows={chosen}
        ticks={selectedTicks}
        setTicks={setSelectedTicks}
        query={selectedQuery}
        setQuery={setSelectedQuery}
        title="Selected"
      />

      {ordered && (
        <div className="flex flex-row gap-1 self-center sm:flex-col">
          {(
            [
              ['top', ChevronsUp, 'Move to top'],
              ['up', ChevronUp, 'Move up'],
              ['down', ChevronDown, 'Move down'],
              ['bottom', ChevronsDown, 'Move to bottom'],
            ] as const
          ).map(([to, Icon, label]) => (
            <Button
              key={to}
              variant="ghost"
              className="h-8 px-2"
              onClick={() => move(to)}
              disabled={selectedTicks.length === 0}
              aria-label={label}
              title={label}
            >
              <Icon className="size-4" />
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
